import { Fragment, useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { observer } from 'mobx-react-lite';
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, IconButton, LinearProgress, Typography } from '@mui/material';

import { isArray, isNumber, isNumeric, useMount, useToggle } from '@cutwise/utils';

import { useTopicStore, useTopicViewerStore } from '@/dmdrive/topic/components/Topic/TopicStore';
import { BackdropLoader } from '@/dmdrive/ui-kit/components';
import SwitchField from '@/dmdrive/ui-kit/components/SwitchField/SwitchField';

import { LeftSidebarBlock } from '../LeftSidebar.styles';

import {
  RecognitionControls,
  RecognitionFitness,
  RecognitionLineChart,
  RecognitionProgress,
  RecognitionProgressBar,
  RecognitionProgressIndicator,
} from './Recognition.styles';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

function Recognition() {
  const { topic, isControlDisabled, recognitionUpdateTrigger } = useTopicStore();
  const {
    recognitionState,
    startRecognition,
    stopRecognition,
    isRecognitionStarted,
    //shouldShowMaxDistancePoints,
    useNonSymmetricMetric,
    toggleNonSymmetricMetric,
    viewerSettings,
    isViewerReady,
  } = useTopicViewerStore();

  const { switchModelsRecognition: shouldSwitchModelsRecognition,  showMaxPoints: shouldShowMaxDistancePoints, apply } = viewerSettings;

  const isRecognitionDataExists = isArray(recognitionState) && recognitionState!.length > 1;

  const [shouldShowRecognitionChart, showRecognitionChart, hideRecognitionChart, toggleRecognitionChart] = useToggle();

  const [chartData, setChartData] = useState<ChartData<'line'>>({ labels: [], datasets: [] });

  const switchModelsForRecognition = useCallback(() => {
    apply({
      switchModelsRecognition: !shouldSwitchModelsRecognition,
    });
  }, [shouldSwitchModelsRecognition]);

  const toggleShowMaxDistancePoints = useCallback(() => {
    apply({
      showMaxPoints: !shouldShowMaxDistancePoints,
    });
  }, [shouldShowMaxDistancePoints]);

  const onStartRecognition = useCallback(() => {
    showRecognitionChart();
    startRecognition();
  }, []);

  const onStopRecognition = useCallback(() => {
    stopRecognition();

    if (!isRecognitionDataExists) {
      hideRecognitionChart();
    }
  }, [isRecognitionDataExists]);

  useEffect(() => {
    if (isRecognitionDataExists) {
      const dataset = recognitionState!.filter((item) => item.fitness !== Infinity);

      setChartData({
        labels: dataset.map(({ step }) => step),
        datasets: [
          {
            label: 'Recognition',
            data: dataset.map((item) => item.fitness),
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderColor: 'rgba(0,0,0,0.5)',
            borderWidth: 1,
            pointRadius: 0,
            fill: 'origin',
          },
        ],
      });
    }
  }, [JSON.stringify(recognitionState)]);

  useMount(() => {
    if (isRecognitionDataExists) {
      showRecognitionChart();
    }
  });

  // Force re-render when recognitionUpdateTrigger changes
  useEffect(() => {
    // This effect will run whenever recognitionUpdateTrigger changes
    // which will force the component to re-render and show updated fitness
  }, [recognitionUpdateTrigger]);

  const lastValue = isRecognitionDataExists ? recognitionState![recognitionState!.length - 1] : null;
  const value = lastValue ? Math.round((lastValue.step * 100) / lastValue.maxStep) : 0;

  return (
    <div>
      <LeftSidebarBlock isHeader>
        <Typography variant="h6">Recognition</Typography>
      </LeftSidebarBlock>

      <LeftSidebarBlock>
        <RecognitionControls>
          <LoadingButton
            size="small"
            onClick={onStartRecognition}
            variant="contained"
            loading={isRecognitionStarted}
            disabled={isControlDisabled || !isViewerReady}
          >
            Start
          </LoadingButton>

          {isRecognitionStarted && (
            <Fragment>
              <Button onClick={onStopRecognition}>Stop</Button>

              <RecognitionFitness>
                {lastValue?.fitness && isNumeric(lastValue.fitness) && (
                  <Typography variant="caption">
                    <span>fit: </span>

                    <span>{lastValue.fitness.toFixed(3)}</span>
                  </Typography>
                )}
              </RecognitionFitness>
            </Fragment>
          )}

          {!isRecognitionStarted && isNumber(topic?.fitness) && topic!.fitness! >= 0 && (
            <Typography>
              <span>Fit level: </span>

              <span>{topic!.fitness!.toFixed(3)}</span>
            </Typography>
          )}

          {isRecognitionDataExists && (
            <IconButton sx={{ marginLeft: 'auto' }} onClick={toggleRecognitionChart}>
              {shouldShowRecognitionChart ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </RecognitionControls>

        {shouldShowRecognitionChart && (
          <RecognitionLineChart>
            {!isRecognitionDataExists && <BackdropLoader isLoading={isRecognitionStarted} />}

            {isRecognitionStarted && (
              <RecognitionProgress>
                <RecognitionProgressBar>
                  <LinearProgress variant="determinate" value={value} />
                </RecognitionProgressBar>

                <RecognitionProgressIndicator>
                  <Typography variant="body2">{`${value}%`}</Typography>
                </RecognitionProgressIndicator>
              </RecognitionProgress>
            )}

            <Line
              data={chartData}
              options={{
                animation: {
                  duration: 0,
                },
              }}
            />
          </RecognitionLineChart>
        )}

        <div>
          <SwitchField
            label="Switch target"
            checked={shouldSwitchModelsRecognition}
            onChange={switchModelsForRecognition}
            size="small"
            disabled={isControlDisabled}
          />
        </div>

        <div>
          <SwitchField
            label="Use non symmetric metric"
            checked={useNonSymmetricMetric}
            onChange={toggleNonSymmetricMetric}
            size="small"
            disabled={isControlDisabled}
          />
        </div>

        <div>
          <SwitchField
            label="Show max distance point"
            checked={shouldShowMaxDistancePoints}
            onChange={toggleShowMaxDistancePoints}
            size="small"
            disabled={isControlDisabled}
          />
        </div>
      </LeftSidebarBlock>
    </div>
  );
}

export default observer(Recognition);
